/* eslint-disable no-unused-vars */
import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Table, SelectTamanoPagina, Tooltip, Button } from 'components';
import './TablaDescargasPublicas.scss';

import { filterList } from 'utils/filterList';
import { generateId } from 'utils/idGenerator';
import { base64toBlob } from 'utils/file';
import { downloadExcelPublicDowloads } from 'actions/files.actions';
import { getZipPublico } from 'actions/pdf.actions';
import { PROCESOS_ESPECIFICOS } from 'constants/nombresCatalogos';
import { selectCatalogByName } from 'selectors/catalogos.selectors';
import { selectPublicDownloads } from 'selectors/publicDownloads.selector';
import { message, Spin } from 'antd';
import Icono from 'components/Icono/Icono';
import { DownloadOutlined } from '@ant-design/icons';
import iconFileSave from 'assets/images/file_save.svg';

const TablaDescargasPublicas = ({ mainFilters }) => {
  const dispatch = useDispatch();
  const [filters] = useState({});
  // Removed unused state
  const [messageApi] = message.useMessage();

  const procesosEspe = useSelector(selectCatalogByName(PROCESOS_ESPECIFICOS));

  const { loading, data } = useSelector(selectPublicDownloads);

  const catalogosStore = {};
  catalogosStore.procesosEspe = procesosEspe;

  const showTotal = (total, range) => {
    return `Mostrando del ${range[0]} al ${range[1]} de ${total} registros`;
  };

  const [pagination, setPagination] = useState({
    position: ['bottomRight'],
    showQuickJumper: true,
    size: 'small',
    pageSize: 10,
    current: 1,
    showTotal,
    total: 0,
    showSizeChanger: false
  });

  const handleChangePageSize = (pageSize) => {
    setPagination((prevPagination) => ({ ...prevPagination, pageSize }));
  };

  // Se filtran los resultados buscados
  const filteredList = useMemo(() => {
    let filteredData = [];
    if (data && data.length > 0) {
      filteredData = data;
      filteredData.sort((a, b) => b.folio - a.folio);
    }
    return filteredData;
  }, [data, mainFilters]);

  const dataList = useMemo(() => {
    return filterList(filteredList, filters);
  }, [filteredList, filters]);

  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      const { success, response } = await dispatch(
        downloadExcelPublicDowloads({ reporteConsultaPublica: dataList })
      );
      if (response !== null && response !== undefined && success) {
        if (response.data.code === 200) {
          if (response.data.excel64) {
            const url = window.URL.createObjectURL(
              base64toBlob(response.data.excel64.base64, response.data.excel64.tipo)
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', response.data.excel64.nombre);
            link.click();
          }
        }
      }
      setDownloadingExcel(false);
    } catch (error) {
      setDownloadingExcel(false);
    }
  };

  const construirUbicacion = (_, row) => {
    switch (row.idCMonitoreo) {
      case 1:
        return `${row.calle ? row.calle : ''} ${row.numero ? row.numero : ''} ${
          row.latitud ? row.latitud : ''
        } ${row.longitud ? row.longitud : ''}`;
      case 2:
        return `N/A`;
      case 3:
        return `N/A`;
      case 4:
        return `${row.calle ? row.calle : ''} ${row.numero ? row.numero : ''} ${
          row.latitud ? row.latitud : ''
        } ${row.longitud ? row.longitud : ''}`;
      default:
        return `N/A`;
    }
  };

  const construirValor = (text) => {
    if (text !== null && text !== undefined && text.trim().length > 0) {
      return text;
    }
    return 'N/A';
  };

  const [pdfActa, setPdfActa] = useState(null);

  function downloadBase64File(base64Data, fileName) {
    const linkSource = `data:application/zip;base64,${base64Data}`;
    const downloadLink = document.createElement('a');
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  }

  const handleGetPdfActa = async (idMonitoreo, tipoMonitoreoP, tipoActaP, so, folio) => {
    const payload = {
      tipoMonitoreo: tipoMonitoreoP,
      tipoActa: null,
      tipoDescarga: 1,
      listaIdMonitoreos: [idMonitoreo],
      testado: 1,
      idSujetoObligado: so
    };
    try {
      const { success, response } = await dispatch(getZipPublico(payload));
      const { archivo } = response.data;
      if (success) {
        setPdfActa(archivo);
        downloadBase64File(archivo, `Monitoreo_${folio}.zip`);
      }
    } catch (error) {
      messageApi.error('Error al obtener el PDF', 3);
    }
  };

  const handleButtonClick = (fila, tipoActa) => {
    if (fila !== undefined) {
      const { idMonitoreo, idCActa, idSujetoObligado, idCMonitoreo, folio } = fila;
      handleGetPdfActa(idMonitoreo, idCMonitoreo, tipoActa, idSujetoObligado, folio);
    } else {
      console.error('row is undefined');
    }
  };

  const renderActions = (_, row) => (
    <div className="actions-container">
      <Tooltip
        title="Descargar Archivo"
        className="tooltip-acciones btn-deleteoutlined"
        placement="topLeft"
        arrow={{ pointAtCenter: true }}>
        <Button
          type="primary"
          onClick={() => {
            messageApi.open({
              content: <Spin />
            });
            handleButtonClick(row, 1);
          }}
          // icon={<DownloadOutlined />}
          icon={<img src={iconFileSave} alt="icon" width="24" />}
          ghost
        />
      </Tooltip>
    </div>
  );

  const columns = [
    {
      title: 'Folio de monitoreo:',
      key: 'folio',
      dataIndex: 'folio',
      align: 'center',
      width: '8%',
      render: construirValor
    },
    {
      title: 'Entidad:',
      key: 'nombreEstado',
      dataIndex: 'nombreEstado',
      width: '9%',
      render: construirValor
    },
    {
      title: 'Ubicación:',
      key: 'ubicacion',
      dataIndex: 'ubicacion',
      width: '15%',
      render: construirUbicacion
    },
    {
      title: 'Nombre del beneficiado:',
      key: 'beneficiado',
      dataIndex: 'beneficiado',
      width: '10%',
      render: construirValor
    },
    {
      title: 'Cargo:',
      key: 'cargo',
      dataIndex: 'cargo',
      width: '10%',
      render: construirValor
    },
    {
      title: 'Sujeto Obligado:',
      key: 'sujetoObligado',
      dataIndex: 'sujetoObligado',
      width: '10%',
      render: construirValor
    },
    {
      title: 'Monitoreo:',
      key: 'monitoreo',
      dataIndex: 'monitoreo',
      width: '10%',
      render: construirValor
    },
    {
      title: 'Hallazgo:',
      key: 'nombreHallazgo',
      dataIndex: 'nombreHallazgo',
      width: '10%',
      render: construirValor
    },
    {
      title: 'Proceso Electoral:',
      key: 'descripcionProcesoGral',
      dataIndex: 'descripcionProcesoGral',
      width: '10%',
      render: construirValor
    },
    {
      title: 'Ámbito:',
      key: 'ambito',
      dataIndex: 'ambito',
      width: '8%',
      render: construirValor
    },
    {
      title: 'Acciones',
      dataIndex: 'actions',
      align: 'center',
      width: '8%',
      key: 'actions',
      render: renderActions
    }
  ];

  return (
    <div className="tabla-monitoreo">
      <div className="table-header">
        <div className="footer-page-pagination">
          <SelectTamanoPagina
            value={pagination.pageSize}
            onChange={handleChangePageSize}
            disableBottomExcel={downloadingExcel}
            labelExcelButton="Descargar"
            onClick={handleDownloadExcel}
          />
        </div>
      </div>
      <Table
        scroll={{ x: 'max-parent' }}
        bordered={false}
        columns={columns}
        dataSource={dataList}
        loading={loading}
        rowKey={generateId}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize: pageSize || prev.pageSize
            }));
          }
        }}
      />
    </div>
  );
};

export default TablaDescargasPublicas;

TablaDescargasPublicas.propTypes = {
  mainFilters: PropTypes.shape({
    fechaInicio: PropTypes.shape({}),
    fechaFin: PropTypes.shape({}),
    idProcesoGral: PropTypes.number
  }).isRequired,
  seleccion: PropTypes.shape({
    current: PropTypes.shape({})
  }).isRequired
};
